/**
 * Anonymous usage counts and error reports, so Nameling can be improved and its bugs found.
 *
 * Nothing sent identifies anyone: no ID of any kind, no names swiped or typed, nothing about the
 * device beyond "iPhone app", "website" or "home-screen app", the app's build and the location
 * setting. The server keeps no IP addresses (see netlify/lib/stats-core.ts).
 *
 * Nothing is sent without a yes: the deck asks once (SwipeView), and Settings can change it.
 * Until then counts wait in memory on the phone; a no throws them away. With a yes they go in
 * one small batch when the app is put away, or every two minutes while it's open. The preview
 * never sends any.
 */

const EVENT_RE = /^[a-z]{2,16}(\.[a-z0-9_-]{1,16})?$/;
const FLUSH_MS = 120_000;
const MAX_ERRORS = 5;

interface Config {
  /** "yes" to send, "no" to drop, "ask" while the question hasn't been answered */
  consent: () => "yes" | "no" | "ask";
  country: () => string;
  endpoint: string;
}
let cfg: Config | null = null;
const counts = new Map<string, number>();
let errors: { m: string; w: string }[] = [];
let reported = 0;
let timer = 0;

const platform = (): "ios" | "pwa" | "web" =>
  __NATIVE__ ? "ios" : typeof matchMedia === "function" && matchMedia("(display-mode: standalone)").matches ? "pwa" : "web";

const consent = () => (__PREVIEW__ || !cfg ? "no" : cfg.consent());
const on = () => consent() === "yes";
/** collecting (in memory) is fine until someone says no; sending waits for a yes */
const collecting = () => consent() !== "no";

/** Count something that happened: "open", "decide.like", "pair.join"... */
export function count(event: string, n = 1): void {
  if (!collecting() || !EVENT_RE.test(event)) return;
  counts.set(event, Math.min(5000, (counts.get(event) ?? 0) + n));
  schedule();
}

// a dropped connection or a stopped request isn't a bug
const NOISE = /ResizeObserver loop|^Script error\.?$|Failed to fetch|Load failed|NetworkError|AbortError|cancel/i;

/** Report an error that reached the user (or nearly did). `where` is the part of the app. */
export function reportError(where: string, err: unknown): void {
  if (!collecting() || reported >= MAX_ERRORS) return;
  const e = err instanceof Error ? err : new Error(String(err ?? "unknown"));
  const first = `${e.name}: ${e.message}`.split("\n")[0];
  if (NOISE.test(first)) return;
  // the first line of the stack says where; script URLs carry nothing personal
  const at = (e.stack ?? "").split("\n").find((l) => /\d+:\d+/.test(l) && !l.includes(first))?.trim() ?? "";
  reported++;
  errors.push({ m: `${first}${at ? ` ${at}` : ""}`.slice(0, 300), w: where.slice(0, 60) });
  schedule();
}

function schedule(): void {
  if (!timer && on()) timer = window.setTimeout(flush, FLUSH_MS);
}

/** The answer changed: a yes sends what's waiting soon, a no throws it away. */
export function consentChanged(): void {
  if (consent() === "no") {
    counts.clear();
    errors = [];
  } else if (on() && (counts.size || errors.length)) {
    window.clearTimeout(timer);
    timer = window.setTimeout(flush, 5000);
  }
}

/** Send what's collected, if anything (and only with a yes). */
export function flush(): void {
  window.clearTimeout(timer);
  timer = 0;
  if (!cfg || !on() || (!counts.size && !errors.length)) return;
  const body = JSON.stringify({
    p: platform(),
    v: typeof __BUILD_ID__ === "string" ? __BUILD_ID__ : "dev",
    c: cfg.country(),
    e: Object.fromEntries(counts),
    x: errors,
  });
  counts.clear();
  errors = [];
  // plain text keeps it a simple request: no preflight, even from the iPhone app
  const blob = new Blob([body], { type: "text/plain" });
  try {
    if (navigator.sendBeacon?.(cfg.endpoint, blob)) return;
  } catch {
    /* fall through */
  }
  fetch(cfg.endpoint, { method: "POST", body: blob, keepalive: true, mode: "no-cors" }).catch(() => {});
}

export function initTelemetry(c: Config): void {
  cfg = c;
  if (__PREVIEW__) return;
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "hidden") flush();
  });
  window.addEventListener("pagehide", flush);
  window.addEventListener("error", (ev) => reportError("page", ev.error ?? ev.message));
  window.addEventListener("unhandledrejection", (ev) => reportError("promise", ev.reason));
}
